<template>
  <div v-if="isLoading">
    <span class="loading loading-spinner loading-lg text-base-content/60"></span>
  </div>
  <div v-else class="relative">
    <div class="flex justify-end items-center mb-6">
      <button
        v-if="tripInfo.status == 'tripStatus6'"
        class="btn btn-success btn-sm mr-2"
        title="Atšaukti pavėžėjimą"
        @click="confirmTrip(tripInfo.id)"
      >
        <DefaultIcon name="check" size="w-4 h-4" /> Patvirtinti
      </button>
      <button
        v-if="
          tripInfo.status == 'tripStatus1' || tripInfo.status == 'tripStatus6'
        "
        class="btn btn-outline btn-sm"
        title="Atšaukti pavėžėjimą"
        @click="cancelTrip(tripInfo.id)"
      >
        <DefaultIcon name="xMark" size="w-4 h-4" /> Atšaukti
      </button>
      <router-link
        v-if="tripInfo.status == 'tripStatus3'"
        :to="'/trips/' + tripInfo.id + '/edit'"
        class="btn btn-outline btn-sm"
        title="Redaguoti pavėžėjimo informacija"
      >
        <DefaultIcon name="editPencil" size="w-4 h-4" /> Redaguoti
      </router-link>
    </div>

    <!-- Review Sections -->
    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Pavėžėjimo informacija
      </div>
      <div class="collapse-content">
        <div class="px-2 mt-3">
          <TripSummary :tripData="tripInfo" type="trip" />
        </div>
      </div>
    </div>

    <!-- Route Sections -->

    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Pavėžėjimo maršrutas
      </div>
      <div class="collapse-content">
        <div v-if="tripInfo && tripInfo.tripRoute" class="my-3 px-2">
          <p class="font-medium mb-3">Maršruto informacija</p>
          <ol class="text-sm">
            <li>
              Maršruto ilgis (km):
              {{ tripInfo.tripRoute.length?.toFixed(2) ?? "Nėra duomenų" }}
            </li>
            <li>
              Maršruto trukmė (val):
              {{
                tripInfo.tripRoute.time
                  ? secondsToHours(tripInfo.tripRoute.time)
                  : "Nėra duomenų"
              }}
            </li>
          </ol>
        </div>
        <div class="px-2 mt-6">
          <Map
            :waypoints-geometry="tripInfo.tripWaypoints"
            :route-geometry="routeGeometry"
            :center="[21.39, 55.71]"
            :zoom="10"
            class="rounded-md"
          />
        </div>
      </div>
    </div>

    <!-- Additional Info Sections -->
    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Papildoma informacija
      </div>
      <div class="collapse-content">
        <div class="my-3 px-2">
          <p class="font-medium mb-1">Pavėžėjimo redagavimo istorija</p>
          <ol class="text-sm">
            <li>
              Sukurta: {{ tripInfo.created_at ?? "Nėra duomenų" }},
              {{ tripInfo.created_by ?? "Nėra duomenų" }}
            </li>
            <li>
              Redaguota: {{ tripInfo.updated_at ?? "Nėra duomenų" }},
              {{ tripInfo.updated_by ?? "Nėra duomenų" }}
            </li>
          </ol>
        </div>
        <div class="my-3 px-2">
          <p class="font-medium mb-1">Klientų informavimo istorija</p>

          <ol class="text-sm">
            <li v-if="tripInfo.send_sms_registration">
              Patvirtinimo SMS žinutė:
              {{ tripInfo.created_at ?? "Nėra duomenų" }}
            </li>
            <li v-else>Klientų informavimas patvirtinimo žinute išjungtas</li>
            <li v-if="tripInfo.send_sms_reminder">
              Priminimo SMS žinutė:
              {{ tripInfo.reminder_sent_at ?? "Nėra duomenų" }}
            </li>
            <li v-else>Klientų informavimas priminimo žinute išjungtas</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
  <Toast ref="toast" />
</template>

<script setup>
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useGoBack } from "@/composables/navigation/useGoBack";
import { useTripStore } from "@/stores/trips";
import TripSummary from "@/components/trips/TripSummary.vue";
import Map from "@/components/maps/MapComponent.vue";
import { useRouteCalculation } from "@/composables/routing/useRouteCalculation";
import { useTimeManagement } from "@/composables/time/useTimeManagement";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Toast from "@/components/ui/alerts/ToastComponent.vue";

const route = useRoute();
const goBack = useGoBack();
const tripStore = useTripStore();
const tripId = route.params.id;

const { calculateRoute } = useRouteCalculation();
const { secondsToHours } = useTimeManagement();

const isLoading = ref(true);
const tripInfo = ref(null);
const toast = ref(null);

// Fetch and transform data
const loadTripData = async () => {
  try {
    isLoading.value = true;
    const response = await tripStore.fetchTripById(tripId);
    tripInfo.value = response;
    await updateTravelRoute();
  } catch (error) {
    console.error("Error fetching trip data:", error);
    // Handle error appropriately
  } finally {
    isLoading.value = false;
  }
};

const routeGeometry = ref(null);

const updateTravelRoute = async () => {
  try {
    if (tripInfo.value.tripWaypoints.length > 1) {
      // Calc route
      // Calculate route if waypoints exist
      if (tripInfo.value.tripWaypoints?.length > 1) {
        const result = await calculateRoute({
          waypoints: tripInfo.value.tripWaypoints,
          travelPoint:
            tripInfo.value.travelLat && tripInfo.value.travelLng
              ? {
                  lat: tripInfo.value.travelLat,
                  lng: tripInfo.value.travelLng,
                }
              : null,
        });

        // Update trip info with the calculated route
        tripInfo.value.tripRoute = result.summary;
        routeGeometry.value = result.geometry;
      }
    } else {
      routeGeometry.value = null;
      tripInfo.value.tripRoute = [];
    }
  } catch (error) {
    console.error("Error updating trip waypoints:", error);
    // Handle error appropriately - maybe emit an event or show a notification
  }
};

loadTripData();

// Cancel trip by setting travel id to null

const cancelTrip = async (tripId) => {
  if (confirm("Ar jūs tikrai norite atšaukti pavėžėjimą")) {
    try {
      await tripStore.cancelTrip(tripId);

      toast.value.success("Pavėžėjimas sėkmingai atšauktas");

      // Redirect to created trip
      setTimeout(() => {
        goBack("/trips");
      }, 3000);
    } catch (error) {
      console.error("Failed to cancel trip");
    }
  }
};

// Cconfirm trip by updating trip status

const confirmTrip = async (tripId) => {
  if (confirm("Ar jūs tikrai norite patvirtinti pavėžėjimą")) {
    try {
      await tripStore.confirmTrip(tripId);

      toast.value.success("Pavėžėjimas sėkmingai patvirtintas");

      // Redirect to created trip
      setTimeout(() => {
        goBack("/trips");
      }, 3000);
    } catch (error) {
      console.error("Failed to confirm trip");
    }
  }
};
</script>
